<template>
  <div>
    <el-dialog
      title="额度美化"
      :visible.sync="configVisible"
      width="760px"
      class="quota-dialog"
      :close-on-click-modal="false"
      :close-on-press-escape="!saving"
      :show-close="!saving"
    >
      <el-form v-loading="loading" :model="form" label-position="left" label-width="172px" size="small" class="quota-config-form" :disabled="loading || saving || readOnly">
        <el-form-item label="状态" :required="!readOnly">
          <el-radio-group v-model="form.enabled">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="美化字段" :required="!readOnly">
          <el-select value="usedQuota" disabled class="beautify-field" aria-label="美化字段">
            <el-option label="已用额度" value="usedQuota" />
          </el-select>
        </el-form-item>
        <el-form-item label="执行频率" :required="!readOnly && form.enabled === 1" :error="fieldError('frequencyMinutes')">
          <el-input v-model.trim="form.frequencyMinutes" maxlength="10" aria-label="执行频率">
            <template slot="append">分钟</template>
          </el-input>
        </el-form-item>
        <div class="condition-title">执行条件</div>
        <div class="condition-line">
          <span>近</span>
          <el-form-item label-width="0" class="condition-field" :error="fieldError('windowMinutes')">
            <el-input v-model.trim="form.windowMinutes" maxlength="10" aria-label="执行条件区间（分钟）" />
          </el-form-item>
          <span>分钟内，已用额度更新次数不达</span>
          <el-form-item label-width="0" class="condition-field" :error="fieldError('threshold')">
            <el-input v-model.trim="form.threshold" maxlength="10" aria-label="已用额度更新次数" />
          </el-form-item>
          <span>次时执行</span>
        </div>
        <el-form-item label="执行扣减额度区间" :required="!readOnly && form.enabled === 1">
          <div class="amount-range">
            <el-form-item label-width="0" class="amount-field" :error="fieldError('minAmount')">
              <el-input v-model.trim="form.minAmount" maxlength="6" aria-label="额度最小值" />
            </el-form-item>
            <span class="range-separator">－</span>
            <el-form-item label-width="0" class="amount-field" :error="fieldError('maxAmount')">
              <el-input v-model.trim="form.maxAmount" maxlength="6" aria-label="额度最大值">
                <template slot="append">USDT</template>
              </el-input>
            </el-form-item>
          </div>
        </el-form-item>
        <el-alert
          title="满足执行条件后，系统将按执行频率在配置区间内扣减剩余额度。"
          type="info" show-icon :closable="false" class="quota-tip"
        />
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" :disabled="saving" @click="configVisible = false">{{ readOnly ? '关闭' : '取消' }}</el-button>
        <el-button v-if="!readOnly" type="primary" size="small" :disabled="loading" :loading="saving" @click="save">确定</el-button>
      </div>
    </el-dialog>
    <el-dialog title="额度美化明细" :visible.sync="recordsVisible" width="1180px" class="quota-dialog" :close-on-click-modal="false">
      <el-table v-loading="recordsLoading" :data="records" border size="small" max-height="480">
        <el-table-column label="调整时间" min-width="165">
          <template slot-scope="scope">{{ formatTime(scope.row.createdTime) }}</template>
        </el-table-column>
        <el-table-column label="美化金额" min-width="115" align="right">
          <template slot-scope="scope">{{ amountText(scope.row.amount) }}</template>
        </el-table-column>
        <el-table-column label="调整前已用额度" min-width="135" align="right">
          <template slot-scope="scope">{{ amountText(scope.row.beforeUsedQuota) }}</template>
        </el-table-column>
        <el-table-column label="调整后已用额度" min-width="135" align="right">
          <template slot-scope="scope">{{ amountText(scope.row.afterUsedQuota) }}</template>
        </el-table-column>
        <el-table-column label="调整前剩余额度" min-width="135" align="right">
          <template slot-scope="scope">{{ amountText(scope.row.beforeAvailableQuota) }}</template>
        </el-table-column>
        <el-table-column label="调整后剩余额度" min-width="135" align="right">
          <template slot-scope="scope">{{ amountText(scope.row.afterAvailableQuota) }}</template>
        </el-table-column>
        <el-table-column label="统计区间" min-width="170">
          <template slot-scope="scope">
            <div>{{ formatTime(scope.row.windowStartTime) }}</div>
            <div>{{ formatTime(scope.row.windowEndTime) }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="realGrantCount" label="成功发放次数" min-width="110" align="center" />
        <el-table-column prop="beautifyCount" label="美化次数" width="90" align="center" />
        <el-table-column prop="threshold" label="阈值" width="70" align="center" />
      </el-table>
      <el-pagination
        class="pager" background layout="total, prev, pager, next, sizes"
        :total="total" :page-size="size" :current-page.sync="page" :page-sizes="[10, 20, 50]"
        @current-change="fetchRecords" @size-change="changeSize"
      />
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="recordsVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '../api'

function emptyConfig() {
  return { id: null, enabled: 0, frequencyMinutes: '', windowMinutes: '', threshold: '', minAmount: '', maxAmount: '' }
}

export default {
  name: 'QuotaBeautifyDialog',
  props: {
    formatTime: { type: Function, required: true },
    amountText: { type: Function, required: true },
  },
  data() {
    return {
      configVisible: false,
      readOnly: true,
      loading: false,
      configRequest: 0,
      saving: false,
      showErrors: false,
      form: emptyConfig(),
      recordsVisible: false,
      recordsLoading: false,
      records: [],
      recordActivityId: null,
      recordRequest: 0,
      total: 0,
      page: 1,
      size: 10,
    }
  },
  computed: {
    errors() {
      const errors = {}
      ;['frequencyMinutes', 'windowMinutes', 'threshold'].forEach((key) => {
        if (this.form.enabled === 0 && (this.form[key] === '' || this.form[key] == null)) return
        if (!/^\d+$/.test(String(this.form[key])) || Number(this.form[key]) < 1 || Number(this.form[key]) > 2147483647) {
          errors[key] = '请输入大于0的整数'
        }
      })
      ;['minAmount', 'maxAmount'].forEach((key) => {
        const value = this.form[key]
        if (this.form.enabled === 0 && (value === '' || value == null)) return
        if (!/^\d+(\.\d{1,2})?$/.test(String(value)) || Number(value) < 0.01 || Number(value) > 10000) {
          errors[key] = '请输入0.01至10000 USDT，最多两位小数'
        }
      })
      if (!errors.minAmount && !errors.maxAmount && this.form.minAmount !== '' && this.form.minAmount != null && this.form.maxAmount !== '' && this.form.maxAmount != null && Number(this.form.minAmount) > Number(this.form.maxAmount)) {
        errors.maxAmount = '最大值不能小于最小值'
      }
      return errors
    },
  },
  methods: {
    fieldError(key) {
      return this.showErrors ? this.errors[key] || '' : ''
    },
    async openConfig(row) {
      const request = ++this.configRequest
      this.readOnly = row.status !== 1
      this.form = { ...emptyConfig(), id: row.id }
      this.showErrors = false
      this.configVisible = true
      this.loading = true
      try {
        const res = await api.quotaBeautifyConfig(row.id)
        if (request === this.configRequest && this.configVisible) this.form = { ...emptyConfig(), ...res.data }
      } catch (error) {
        if (request === this.configRequest) this.configVisible = false
      } finally {
        if (request === this.configRequest) this.loading = false
      }
    },
    async save() {
      this.showErrors = true
      if (this.readOnly || this.loading || this.saving || Object.keys(this.errors).length) return
      this.saving = true
      const payload = { id: this.form.id, enabled: this.form.enabled }
      ;['frequencyMinutes', 'windowMinutes', 'threshold', 'minAmount', 'maxAmount'].forEach((key) => {
        const value = this.form[key]
        if (value !== '' && value != null) payload[key] = key.endsWith('Amount') ? String(value) : Number(value)
      })
      try {
        await api.saveQuotaBeautifyConfig(payload)
        this.$message.success('保存成功')
        this.configVisible = false
        this.$emit('saved')
      } finally {
        this.saving = false
      }
    },
    openRecords(row) {
      this.recordActivityId = row.id
      this.page = 1
      this.size = 10
      this.records = []
      this.total = 0
      this.recordsVisible = true
      this.fetchRecords()
    },
    async fetchRecords() {
      const request = ++this.recordRequest
      this.recordsLoading = true
      try {
        const res = await api.quotaBeautifyRecords({ id: this.recordActivityId, page: this.page, size: this.size })
        if (request !== this.recordRequest) return
        this.records = res.data.items || []
        this.total = res.data.total || 0
      } finally {
        if (request === this.recordRequest) this.recordsLoading = false
      }
    },
    changeSize(size) {
      this.size = size
      this.page = 1
      this.fetchRecords()
    },
  },
}
</script>

<style lang="less" scoped>
.quota-dialog {
  /deep/ .el-dialog { max-width: calc(100% - 32px); }
  /deep/ .el-dialog__header { padding: 18px 24px 8px; }
  /deep/ .el-dialog__body { padding: 14px 24px 8px; }
  /deep/ .el-dialog__footer { padding: 12px 24px 16px; border-top: 1px solid #edf1f7; }
  /deep/ .el-form-item__label { color: #303133; }
  /deep/ .el-input-group__append { padding: 0 12px; }
  /deep/ .el-input.is-disabled .el-input__inner { color: #606266; }
  .beautify-field { width: 100%; }
  .condition-title { margin: 4px 0 12px; color: #303133; font-size: 15px; font-weight: 600; }
  .condition-line { display: flex; flex-wrap: wrap; align-items: flex-start; gap: 8px 10px; margin-bottom: 6px; line-height: 32px; color: #303133; }
  .condition-field { flex: 0 0 112px; margin: 0 0 20px; }
  .amount-range { display: grid; grid-template-columns: minmax(0, 1fr) 20px minmax(0, 1fr); gap: 10px; align-items: start; }
  .amount-field { min-width: 0; margin-bottom: 0; }
  .amount-field /deep/ .el-form-item__error { position: static; }
  .range-separator { text-align: center; color: #606266; }
  .quota-tip { margin: 8px 0; background: #e8f4ff; color: #409eff; }
  .quota-tip /deep/ .el-alert__title { font-size: 13px; line-height: 20px; }
  .dialog-footer { display: flex; justify-content: flex-end; gap: 16px; }
  .dialog-footer .el-button + .el-button { margin-left: 0; }
  .pager { margin: 18px 0; overflow-x: auto; }
}
@media (max-width: 600px) {
  .quota-config-form /deep/ .el-form-item__label { width: 100% !important; float: none; text-align: left; line-height: 22px; padding-bottom: 6px; }
  .quota-config-form /deep/ .el-form-item__content { margin-left: 0 !important; }
  .quota-dialog .amount-range { gap: 6px; grid-template-columns: minmax(0, 1fr) 12px minmax(0, 1fr); }
}
</style>
